/**
 * System Health & Settings Page
 *
 * Main admin page for monitoring system health, services status, and configuration.
 * Features: Four-tab interface (Overview | System Info | Services | Health Checks)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  IconServer,
  IconRefresh,
  IconAlertCircle,
  IconActivity,
  IconInfoCircle,
  IconBrandDocker,
  IconHeartbeat,
} from '@tabler/icons-react';
import {
  Container,
  Stack,
  Group,
  Text,
  Title,
  Button,
  Alert,
  Grid,
  Card,
  Tabs,
  Loader,
} from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations';
import { useSystemHealth } from './hooks/useSystemHealth';
import { OverviewTab, SystemInfoTab, ServicesTab, HealthChecksTab } from './components';
import { formatUptime } from './utils/systemHelpers.tsx';

export default function SystemPage() {
  const { healthCheck, systemInfo, services, isLoading, error, setError, fetchAll, startAutoRefresh, stopAutoRefresh } =
    useSystemHealth();

  const [activeTab, setActiveTab] = useState<string>('overview');

  // Auto-refresh on mount
  useEffect(() => {
    fetchAll();
    startAutoRefresh(30000); // 30 seconds
    return () => stopAutoRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate stats for cards
  const stats = useMemo(
    () => ({
      overallStatus: healthCheck?.status || 'Unknown',
      healthyServices: services.filter((s) => s.healthStatus === 'Healthy').length,
      totalServices: services.length,
      healthChecks: Object.keys(healthCheck?.entries || {}).length,
      uptime: systemInfo?.uptime || '0:00:00',
    }),
    [healthCheck, services, systemInfo]
  );

  return (
    <Container size="xl" py="xl">
      <FadeIn delay={100}>
        <Stack gap="xl">
          {/* 1. HEADER SECTION */}
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="sm">
                <IconServer size={32} />
                <Title order={2}>System Health & Settings</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Monitor system health, services status, and configuration
              </Text>
            </Stack>
            <Button onClick={fetchAll} loading={isLoading} leftSection={<IconRefresh size={16} />} variant="subtle">
              Refresh
            </Button>
          </Group>

          {/* 2. ERROR ALERT */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* 3. STATS CARDS (4 gradient cards) */}
          <SlideIn direction="up" delay={150}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card variant="gradient" gradientVariant="primary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        System Status
                      </Text>
                      <IconHeartbeat size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.overallStatus}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card variant="gradient" gradientVariant="secondary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Services
                      </Text>
                      <IconBrandDocker size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.healthyServices} / {stats.totalServices}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card variant="gradient" gradientVariant="info" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Health Checks
                      </Text>
                      <IconActivity size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.healthChecks}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card variant="gradient" gradientVariant="success" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Uptime
                      </Text>
                      <IconInfoCircle size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {formatUptime(stats.uptime)}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </SlideIn>

          {/* 4. TABS SECTION */}
          <SlideIn direction="up" delay={200}>
            <Card>
              <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
                <Tabs.List>
                  <Tabs.Tab value="overview" leftSection={<IconHeartbeat size={16} />}>
                    Overview
                  </Tabs.Tab>
                  <Tabs.Tab value="info" leftSection={<IconInfoCircle size={16} />}>
                    System Info
                  </Tabs.Tab>
                  <Tabs.Tab value="services" leftSection={<IconBrandDocker size={16} />}>
                    Services
                  </Tabs.Tab>
                  <Tabs.Tab value="health" leftSection={<IconActivity size={16} />}>
                    Health Checks
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="md">
                  {isLoading ? (
                    <Group justify="center" p="xl">
                      <Loader size="lg" />
                    </Group>
                  ) : (
                    <OverviewTab healthCheck={healthCheck} systemInfo={systemInfo} onRefresh={fetchAll} />
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="info" pt="md">
                  {isLoading ? (
                    <Group justify="center" p="xl">
                      <Loader size="lg" />
                    </Group>
                  ) : (
                    <SystemInfoTab systemInfo={systemInfo} onRefresh={fetchAll} />
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="services" pt="md">
                  {isLoading ? (
                    <Group justify="center" p="xl">
                      <Loader size="lg" />
                    </Group>
                  ) : (
                    <ServicesTab services={services} onRefresh={fetchAll} />
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="health" pt="md">
                  {isLoading ? (
                    <Group justify="center" p="xl">
                      <Loader size="lg" />
                    </Group>
                  ) : (
                    <HealthChecksTab entries={healthCheck?.entries || {}} onRefresh={fetchAll} />
                  )}
                </Tabs.Panel>
              </Tabs>
            </Card>
          </SlideIn>
        </Stack>
      </FadeIn>
    </Container>
  );
}
